import {
  Directive,
  Input,
  OnInit,
  OnDestroy,
  inject,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { FormGroupDirective, AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { RadioButtonBinaryOptions } from '@shared/enums/radio-buttons-binary-options.enum';
import { SectionsService } from '@app/shared/dasboard-sections/services/sections.service';

import { GenericApplicationSections } from '@shared/interfaces/agnostic-sections.type';

export interface ConditionalFieldConfig {
  /** The form control name that triggers the conditional logic */
  triggerControl: string;
  /** The form control names that are affected by the trigger */
  targetControls: string[];
  /** The value that enables the target controls (default: 'Yes') */
  enableValue?: string;
  /** Whether to show/hide the fields or just enable/disable them (default: true) */
  hideWhenDisabled?: boolean;
  /** The section to check for locking (optional) */
  section?: GenericApplicationSections;
  /** Whether to clear values when disabling (default: true) */
  clearValuesWhenDisabled?: boolean;
  /** Whether to mark fields as touched when enabling (default: true) */
  markTouchedWhenEnabled?: boolean;
}

@Directive({
  selector: '[appConditionalFields]',
  standalone: true,
})
export class ConditionalFieldsDirective implements OnInit, OnDestroy {
  @Input('appConditionalFields') config!: ConditionalFieldConfig;

  private readonly _formGroupDirective = inject(FormGroupDirective, {
    optional: false,
  });
  private readonly _sectionsService = inject(SectionsService);
  private readonly _elementRef = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);

  private _subscription?: Subscription;
  private _isEnabled = false;

  ngOnInit() {
    if (!this.config) {
      console.warn('ConditionalFieldsDirective: config is required');
      return;
    }

    this._setupConditionalLogic();
  }

  ngOnDestroy() {
    this._subscription?.unsubscribe();
  }

  private _setupConditionalLogic() {
    const triggerControl = this._formGroupDirective.form.get(
      this.config.triggerControl,
    );

    if (!triggerControl) {
      console.warn(
        `ConditionalFieldsDirective: trigger control '${this.config.triggerControl}' not found`,
      );
      return;
    }

    const enableValue = this.config.enableValue ?? RadioButtonBinaryOptions.Yes;
    const hideWhenDisabled = this.config.hideWhenDisabled ?? true;
    const clearValuesWhenDisabled = this.config.clearValuesWhenDisabled ?? true;
    const markTouchedWhenEnabled = this.config.markTouchedWhenEnabled ?? true;

    // Subscribe to trigger control changes
    this._subscription = triggerControl.valueChanges.subscribe((value) => {
      const shouldEnable = value === enableValue;
      this._isEnabled = shouldEnable;

      this.config.targetControls.forEach((controlName) => {
        const targetControl = this._formGroupDirective.form.get(controlName);
        if (!targetControl) {
          console.warn(
            `ConditionalFieldsDirective: target control '${controlName}' not found`,
          );
          return;
        }

        this._updateControlState(
          targetControl,
          shouldEnable,
          clearValuesWhenDisabled,
          markTouchedWhenEnabled,
        );

        // Handle visibility if configured
        if (hideWhenDisabled) {
          this._updateFieldVisibility(controlName, shouldEnable);
        }
      });
    });

    // Initial state setup
    const initialValue = triggerControl.value;
    const shouldEnable = initialValue === enableValue;
    this._isEnabled = shouldEnable;

    this.config.targetControls.forEach((controlName) => {
      const targetControl = this._formGroupDirective.form.get(controlName);
      if (targetControl) {
        this._updateControlState(
          targetControl,
          shouldEnable,
          clearValuesWhenDisabled,
          markTouchedWhenEnabled,
        );

        if (hideWhenDisabled) {
          this._updateFieldVisibility(controlName, shouldEnable);
        }
      }
    });
  }

  private _updateControlState(
    control: AbstractControl,
    shouldEnable: boolean,
    clearValuesWhenDisabled: boolean,
    markTouchedWhenEnabled: boolean,
  ) {
    const isSectionLocked = this.config.section
      ? this._sectionsService.isSectionLocked(this.config.section)
      : false;

    if (shouldEnable) {
      if (!isSectionLocked) {
        control.enable();
      }
      if (markTouchedWhenEnabled) {
        control.markAsTouched();
      }
      control.updateValueAndValidity();
    } else {
      if (clearValuesWhenDisabled) {
        control.setValue(null);
      }
      control.disable();
      control.clearValidators();
      control.markAsUntouched();
      control.updateValueAndValidity();
    }
  }

  private _updateFieldVisibility(controlName: string, shouldShow: boolean) {
    // Find the form field element by looking for the control name in the DOM
    const formField = this._elementRef.nativeElement.querySelector(
      `[formControlName="${controlName}"]`,
    );

    if (formField) {
      const formFieldContainer =
        formField.closest('.form-field-container') ||
        formField.closest('div') ||
        formField.parentElement;

      if (formFieldContainer) {
        if (shouldShow) {
          this._renderer.removeStyle(formFieldContainer, 'display');
        } else {
          this._renderer.setStyle(formFieldContainer, 'display', 'none');
        }
      }
    }
  }

  /** Get the current enabled state */
  get isEnabled(): boolean {
    return this._isEnabled;
  }
}
